import RioOverlayTrigger, { type OverlayTriggerProps } from '@rio-cloud/rio-uikit/OverlayTrigger';
import RioTooltip from '@rio-cloud/rio-uikit/Tooltip';
import classnames from 'classnames';
import type React from 'react';

import type { PropsWithTestId } from '../types';
import './_tooltip.scss';
import type { TooltipWidth } from '@rio-cloud/rio-uikit/Tooltip';

export type TooltipPlacement = NonNullable<OverlayTriggerProps['placement']>;

export type TooltipIconType = 'warning' | 'error';

const ICON_GLYPH: Record<TooltipIconType, string> = {
    warning: 'rioglyph-warning-sign',
    error: 'rioglyph-exclamation-sign',
};

interface TooltipHeaderProps {
    title: string;
    icon?: TooltipIconType;
}

const TooltipHeader = ({ title, icon }: TooltipHeaderProps) => (
    <div className='Tooltip__header' data-tooltip-part='header'>
        {icon && (
            <span
                className={`Tooltip__header-icon Tooltip__header-icon--${icon} rioglyph ${ICON_GLYPH[icon]}`}
                aria-hidden='true'
            />
        )}
        <span className='Tooltip__header-title'>{title}</span>
    </div>
);

interface TooltipMetaProps {
    children: React.ReactNode;
}

const TooltipMeta = ({ children }: TooltipMetaProps) => (
    <p className='Tooltip__meta' data-tooltip-part='meta'>
        {children}
    </p>
);

const TooltipDivider = () => <hr className='Tooltip__divider' data-tooltip-part='divider' />;

interface TooltipContentProps {
    children: React.ReactNode;
}

const TooltipContent = ({ children }: TooltipContentProps) => (
    <p className='Tooltip__content' data-tooltip-part='content'>
        {children}
    </p>
);

export type TooltipVariant = 'rich' | 'plain';

interface TooltipProps {
    trigger: React.ReactNode;
    placement?: TooltipPlacement;
    children: React.ReactNode;
    className?: string;
    innerClassName?: string;
    classNames?: string;
    width?: TooltipWidth;
    /**
     * 'rich' (default) applies the design-system tooltip styling.
     * 'plain' renders the bare RIO tooltip (thinner, e.g. simple label hints).
     */
    variant?: TooltipVariant;
}

type TooltipComponent = React.FC<PropsWithTestId<TooltipProps>> & {
    Header: typeof TooltipHeader;
    Meta: typeof TooltipMeta;
    Divider: typeof TooltipDivider;
    Content: typeof TooltipContent;
};

const Tooltip: TooltipComponent = ({
    trigger,
    placement = 'bottom',
    children,
    className,
    innerClassName,
    classNames,
    width,
    variant = 'rich',
    'data-testid': dataTestId,
}: PropsWithTestId<TooltipProps>) => (
    <RioOverlayTrigger
        placement={placement}
        overlay={
            <RioTooltip
                className={className}
                data-testid={dataTestId ? `${dataTestId}-panel` : undefined}
                innerClassName={variant === 'plain' ? innerClassName : classnames('Tooltip__inner', innerClassName)}
                textAlignment={variant === 'plain' ? undefined : 'left'}
                width={width}
            >
                {children}
            </RioTooltip>
        }
    >
        <span className={classNames} data-testid={dataTestId}>
            {trigger}
        </span>
    </RioOverlayTrigger>
);

Tooltip.Header = TooltipHeader;
Tooltip.Meta = TooltipMeta;
Tooltip.Divider = TooltipDivider;
Tooltip.Content = TooltipContent;

export default Tooltip;
